<template>
  <Dialog
    v-model:open="localVisible"
    modal
    :style="{ width: '100%', maxWidth: '1024px', height: '100%' }"
    :breakpoints="{ '640px': '100vw', '830px': '95vw' }"
    @hide="close"
  >
    <DialogContent class="flex flex-col w-full h-full overflow-hidden p-0 md:h-[85vh] md:w-[90vw] md:max-w-[830px] lg:max-w-[1200px] xl:max-w-[1400px] 2xl:max-w-[1600px] md:rounded-2xl [&>button]:hidden">
      <DialogHeader class="sticky top-0 bg-white dark:bg-zinc-900 z-10 border-b border-zinc-200 dark:border-white/10 p-4 md:p-6 flex flex-row items-center justify-center relative flex-shrink-0">
        <div class="absolute left-4">
          <button 
            class="flex items-center gap-3 h-8 md:h-10 px-2 md:px-3 text-gray-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-white/5 rounded-xl font-bold transition-all group"
            @click="close"
          >
            <div class="flex h-6 w-6 md:h-7 md:w-7 items-center justify-center rounded-lg bg-[hsl(var(--modal-accent-bg))] dark:bg-[hsl(var(--modal-accent-bg)/0.1)] text-[hsl(var(--modal-accent-text))] dark:text-[hsl(var(--modal-accent-text))] transition-transform duration-200 group-hover:-translate-x-1">
              <ArrowLeft class="w-3.5 h-3.5 md:w-4 md:h-4" />
            </div>
            <span class="text-sm md:text-base">Back</span>
          </button>
        </div>
        <DialogTitle class="text-xl font-bold tracking-tight text-center text-gray-900 dark:text-white">Privacy Policy</DialogTitle>
      </DialogHeader>

      <div class="overflow-y-auto flex-1 px-4 py-4 space-y-4">
        <Accordion multiple v-model="expandedValues">
          <AccordionPanel
            v-for="(section, index) in sections"
            :key="index"
            :value="index"
          >
            <AccordionHeader>
              <div class="flex items-center justify-between w-full cursor-pointer select-none">
                <span>{{ section.title }}</span>
              </div>
            </AccordionHeader>
            <AccordionContent>
              <div class="prose dark:prose-invert space-y-2">
                <component :is="section.content" />
              </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 mb-6">
          <p class="mb-1 sm:mb-0"><strong>Effective Date:</strong> 1st October 2025</p>
          <p class="text-sm sm:text-base"><strong>Last Updated:</strong> 25th May 2026</p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ArrowLeft } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'

// Import each section as a separate component
import Section1 from '@/components/app/privacy/sections/Section1.vue'
import Section2 from '@/components/app/privacy/sections/Section2.vue'
import Section3 from '@/components/app/privacy/sections/Section3.vue'
import Section4 from '@/components/app/privacy/sections/Section4.vue'
import Section5 from '@/components/app/privacy/sections/Section5.vue'
import Section6 from '@/components/app/privacy/sections/Section6.vue'
import Section7 from '@/components/app/privacy/sections/Section7.vue'
import Section8 from '@/components/app/privacy/sections/Section8.vue'
import Section9 from '@/components/app/privacy/sections/Section9.vue'
import Section10 from '@/components/app/privacy/sections/Section10.vue'
import Section11 from '@/components/app/privacy/sections/Section11.vue'

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['update:isOpen'])

// Local visible state for Dialog
const localVisible = ref(false)

// Sync props -> local
watch(
  () => props.isOpen,
  (val) => localVisible.value = val,
  { immediate: true }
)

// Sync local -> props (two-way binding)
watch(localVisible, (val) => emit('update:isOpen', val))

const close = () => (localVisible.value = false)

// Track expanded accordion panels
const expandedValues = ref<number[]>([])

// Accordion sections
const sections = [
  { title: '1. Introduction', content: Section1 },
  { title: '2. Information We Collect', content: Section2 },
  { title: '3. How We Use Your Information', content: Section3 },
  { title: '4. Cookies and Local Storage', content: Section4 },
  { title: '5. Data Storage and Security', content: Section5 },
  { title: '6. Data Retention', content: Section6 },
  { title: '7. GDPR Compliance (for EU Users)', content: Section7 },
  { title: '8. Data Sharing', content: Section8 },
  { title: '9. Children’s Privacy', content: Section9 },
  { title: '10. Updates to This Policy', content: Section10 },
  { title: '11. Contact Us', content: Section11 }
]
</script>

<style scoped>
.prose {
  max-height: 75vh;
  overflow-y: auto;
}
.rotate-90 {
  transform: rotate(90deg);
}
</style>